/**
 * palette.ts — The game's colour language, in one place.
 *
 * ## The look: a cute English market town
 *
 * Think a small town on an overcast-but-pleasant day: warm brick and cream
 * render, slate roofs, painted shopfronts, a bit of green. Storybook rather than
 * photoreal — saturated enough to be cheerful, never neon.
 *
 * Three rules keep it coherent. Follow them and new things will look like they
 * belong; break them and they'll stick out:
 *
 * 1. **Warm neutrals do the bulk of the work.** Buildings, pavement, and road
 *    are all slightly warm and desaturated. That's what reads as English light —
 *    a cool grey-blue city reads as somewhere else entirely, which is where this
 *    palette started.
 * 2. **Saturation is a budget, spent on small things.** Doors, shopfronts, the
 *    dog, the critters, effects. If a large surface is vivid, it will fight the
 *    characters for attention and the town stops reading as a backdrop.
 * 3. **Nothing is pure black, white, or grey.** Every neutral is nudged toward
 *    warm. Pure `#888` looks dead next to the rest of this.
 *
 * Gameplay-critical colours (leash tension, markers, effects) are exempt from
 * rule 2 — they must stay legible first and pretty second.
 *
 * Everything is a Three.js hex literal. Import these rather than writing new
 * literals, and give any addition a name that says its *role*, not its hue —
 * `BRICK`, not `REDDISH_BROWN`.
 */

/** Brick, render, and stone — the walls of the town. */
export const WALLS = {
  BRICK: 0xb5674e, // warm red brick
  BRICK_DEEP: 0x9d5340, // older, darker brick for variety
  RENDER_CREAM: 0xe6d5b2, // painted render / Cotswold stone
  RENDER_SAGE: 0x94a988, // muted green painted render
  RENDER_DUCKEGG: 0x93b3ba, // pale blue-green painted render
  RENDER_BUTTER: 0xe8cf94, // soft yellow render
  RENDER_ROSE: 0xd2a394, // dusty pink render
} as const;

/** Roofs, and the bits that sit on them. */
export const ROOFS = {
  SLATE: 0x6a6874, // blue-grey slate, the default
  SLATE_DARK: 0x585765,
  TILE: 0xa8624a, // terracotta pantile
  CHIMNEY: 0xa5715c, // brick chimney stack
  CHIMNEY_POT: 0xc4785c,
  /**
   * How much lighter a tile's face is than the joint around it.
   *
   * A shift of the roof's own colour rather than a hue of its own — a fixed
   * tile colour would clash with terracotta and slate both.
   *
   * This has to be pushed harder than it looks like it should. When the tiles
   * were real raised boxes the shading did half the work; as a texture there's
   * nothing but the tonal step, so a value that looks right on the canvas is
   * invisible on the roof.
   */
  TILE_FACE_LIGHTEN: 0.24,
  /**
   * The rolled tiles capping a ridge or a hip's corner, as a lift of the roof's
   * own colour.
   *
   * Lighter, not darker: these are the parts of the roof standing highest and
   * catching the most sun, and a dark roll on a slate roof just reads as a gap
   * between the slopes — which is precisely the seam it's there to hide.
   */
  RIDGE_LIGHTEN: 0.12,
  /**
   * The fascia board under the eaves, as a *drop* of the roof colour.
   *
   * Its job is to give the roof a visible thickness where it meets the wall.
   * That only works if it's clearly darker than both the tiles above it and the
   * wall below, so the eye reads a shadowed board rather than a stripe of paint.
   */
  EAVES_DARKEN: -0.28,
} as const;

/**
 * Brickwork and stone coursing laid over a wall.
 *
 * Like the roof tiles, these are a *shift* of the wall's own colour rather than
 * a fixed hue: the town has three brick tones and the coursing has to read
 * against all of them.
 */
export const MASONRY = {
  /**
   * How much lighter a brick face sits than the mortar around it.
   *
   * The lit rectangles are the *bricks* and the darker grid between them is the
   * mortar, not the other way round — a grid of light rectangles reads as
   * brickwork where a grid of light lines just reads as a scribble.
   */
  BRICK_FACE_LIGHTEN: 0.3,
  /**
   * The same idea for a paving slab against its joint.
   *
   * Gentler than brickwork. A footway is a large, pale, evenly-lit surface seen
   * from above, where the same contrast that reads as crisp coursing on a wall
   * reads as a harsh grid — and the player walks the length of it, so it is on
   * screen more than any wall in the game.
   */
  PAVING_FACE_LIGHTEN: 0.11,
  /**
   * A cobble sett against its mortar.
   *
   * Stronger than the paving's, and the reason is the unit size rather than the
   * material. A sett is a quarter the area of a paving slab, so at the paving's
   * gentle contrast the pattern dissolves into noise at this camera distance and
   * a cobbled lane reads as a dirty pavement. Still short of brickwork's, which
   * on a large flat surface underfoot reads as a harsh grid.
   */
  COBBLE_FACE_LIGHTEN: 0.19,
  /** Paving and kerb for a front patio — a shade warmer than the public footway. */
  PATIO_PAVING: 0xcfc4ad,
  PATIO_EDGE: 0xb0a48c,
  PATIO_PATH: 0xbfae90, // the path from gate to door
  /**
   * The stone base course every building stands on.
   *
   * Deliberately close to the pavement's own stone: it's the join between the
   * house and the ground, and reading as a continuation of the paving is what
   * makes a building look *set into* the street rather than dropped onto it.
   */
  PLINTH: 0xa79c88,
} as const;

/**
 * Paints for doors and shopfronts. These are the most saturated colours in the
 * town and the main source of its charm — keep them for *small* areas.
 */
export const PAINTS = {
  POSTBOX_RED: 0xb8433c,
  RACING_GREEN: 0x2f5d47,
  NAVY: 0x3a5570,
  TEAL: 0x2f7b7d,
  PLUM: 0x7a4b63,
  MUSTARD: 0xc9963f,
} as const;

/**
 * Front doors. The door itself is deliberately *darker* than any wall or
 * shopfront paint so it reads against all of them, with a pale architrave to
 * outline it — a door in the same colour as the band it sits in vanishes.
 */
export const DOORS = {
  TIMBER: 0x46312a, // dark stained timber
  FRAME: 0xf0e7d6, // painted architrave
  STEP: 0xcdc4b2, // stone doorstep
  // Window sills. Muted stone rather than the bright architrave cream: a sill
  // under every window on four faces adds up, and at frame brightness the whole
  // facade reads as scaffolding.
  SILL: 0xbdb3a0,
  KNOB: 0xd9b45a, // brass
  /**
   * The raised panels on a front door, as a *drop* of the door's own colour.
   *
   * A door this size is barely a dozen pixels wide in play, so the panels can't
   * be a colour of their own without turning into a pattern; a shade of the
   * timber just gives the flat slab somewhere for the light to break.
   */
  PANEL_SHADE: -0.22,
  /** Cast-iron carriage lamps either side of a front door. */
  LAMP: 0x3b3630,
} as const;

/** Windows: warm lit glass, and cool unlit glass. */
export const GLASS = {
  LIT: 0xf7e6b4,
  UNLIT: 0x6c7683,
} as const;

/** The ground plane: pavement, road, grass, and the apron beyond the map. */
export const GROUND_COLORS = {
  PAVING: 0xc7bda9, // footway slabs — pale warm stone
  PAVING_JOINT: 0xb3a893, // lines between slabs
  KERB: 0xa89d87, // kerb face down to the road
  BLOCK_INTERIOR: 0xa79d8a, // yards/forecourts inside the blocks
  TARMAC: 0x5e5b5c, // road surface — warm dark grey, not blue
  ROAD_MARKING: 0xeee8dc, // slightly creamy, not stark white
  /**
   * Double yellows down each kerb. Muted rather than a highway-code yellow: the
   * lines run the length of every street in the town, so at full saturation
   * they'd be the brightest thing on screen and would break the palette's rule
   * that saturation is a budget spent on small things.
   */
  KERB_LINE: 0xd9bb72,
  DRAIN: 0x4a4746, // cast-iron gully cover
  /**
   * Cobbles on the pedestrian lanes cutting through the blocks.
   *
   * Warmer and a shade darker than the footway paving, which is what separates
   * a lane from the pavement at a glance without introducing a new hue — these
   * two surfaces meet end-on wherever a lane comes out onto a street, so if
   * they read as the same material the lane just looks like a wider pavement.
   * Older-looking than the paving on purpose: a cobbled alley is the oldest bit
   * of the town.
   */
  COBBLE: 0xb0a48d,
  COBBLE_JOINT: 0x968b75, // mortar between the setts, deeper than a paving joint
  GRASS: 0x6da45f, // park green
  GRASS_KERB: 0x9c937f, // low surround the grass sits on
  /**
   * Rough ground in the block interiors — the back gardens and side passages.
   *
   * Between the park green and the yard grey, and browner than both. It has to
   * read as *unkept* next to a mown park in the same frame, which a second
   * shade of lawn green would not; and it has to read as *ground* rather than
   * as more hardstanding, which the aggregate it replaced did not.
   */
  SCRUB: 0x8a8b5c,
  /**
   * Bare earth worn through the scrub. Close to it in *value* on purpose: a
   * lighter soil turns every worn patch into a pale blotch and the ground reads
   * as camouflage rather than as grass with the green walked off it.
   */
  SCRUB_SOIL: 0x8a7d5a,
  SURROUNDS: 0x938875,
} as const;

/** Sky and lighting. Bright and soft — a nice day, not harsh midday sun. */
export const ATMOSPHERE = {
  SKY: 0xbcd6e6, // backdrop + fog
  SKY_LIGHT: 0xd8ecf8, // hemisphere light from above
  GROUND_BOUNCE: 0xbaa88a, // warm light bouncing back off the streets
  SUN: 0xfff3e0, // warm sunlight
} as const;

/**
 * The cast: player, dog, and the critters.
 *
 * Each character is **two-tone** — a body colour plus a darker one for the
 * extremities (limbs, ears, tail) and, on the dog, a pale muzzle. That matters
 * more than it sounds: seen from directly above, a single-colour character with
 * rounded clay edges melts into one featureless lump, and the tonal break is
 * what puts the head, paws, and tail back into the silhouette.
 */
export const CHARACTERS = {
  // The walker's red tee. He's the one thing on screen the player must never
  // lose, so he gets the loudest colour in the game — the exception rule 2
  // allows for. It has to stay clear of the lead's red as well as the town's:
  // this sits brighter and warmer, and the lead is deepest exactly where it
  // leaves his hand.
  SHIRT: 0xd2564a,
  SHIRT_DEEP: 0xa8382f, // ribbed collar, sleeve bands, hem — the tonal break
  SKIN: 0xf0c493,
  TROUSERS: 0x2b3a4a, // dark denim
  HAIR: 0x4a382a, // the crown and fringe; the walker goes bare-headed
  BOOT: 0x3b2c22, // walking boots — worn leather
  EYE: 0x2a2119, // warm near-black, shared by walker and dog
  DOG: 0x9a6537, // warm brown
  DOG_DARK: 0x6f4622, // ears, legs, tail, and the saddle patch down its back
  // The pale points: muzzle, the blaze over its skull, socks, chest and tail
  // tip. Seen from above the blaze and saddle are what break up the back, which
  // is otherwise the largest and flattest surface on the model.
  DOG_PALE: 0xd9b98c,
  DOG_NOSE: 0x1c1c1c,
  CAT: 0xdd8b45,
  /**
   * The cat's cream points: chin, bib, paws and the tip of its tail.
   *
   * A ginger cat is one colour from nose to tail in life, and at 40 pixels on
   * screen that is a bean. The cream is invented so there's something for the
   * stripes to sit between — the same argument the dog's pale muzzle and socks
   * won, one size down.
   */
  CAT_PALE: 0xf2ddbd,
  /**
   * Tabby barring across the back. Clearly darker than `CHARACTER_DARK.cat`,
   * which does the ears and paws: the stripes are the one marking that has to
   * read from directly overhead, and at the ear tone they merge into the coat.
   */
  CAT_STRIPE: 0xa85a24,
  FOX: 0xc4553a,
  /** The fox's white bib, cheeks and — the important one — its tail tip. */
  FOX_PALE: 0xf2e9dd,
  /**
   * Black stockings and ear backs. Nearly the darkest thing on any character,
   * and deliberately so: it's what separates a fox from a ginger cat at a
   * glance when both are small and russet.
   */
  FOX_SOCK: 0x3a2a24,
  SQUIRREL: 0xa07f5c,
  /**
   * The squirrel's cream underside, cheeks, ear tufts — and the frosted edge of
   * its tail, which is the one that earns its place. A grey squirrel's plume is
   * rimmed pale, and from overhead that rim is what turns a brown lump on a
   * brown animal's back into a readable fan.
   */
  SQUIRREL_PALE: 0xe8ddc8,
  /**
   * Feral pigeon slate. Cooler and greyer than anything else in the cast, which
   * is the point: the other three critters are all warm browns and russets, and
   * a bird that shares their palette is a fourth brown lump.
   */
  PIGEON: 0x8792a3,
  /**
   * The pale wing panel and rump. A pigeon's wings are visibly lighter than its
   * back, and the pair of black bars below sit *on* this — bars drawn straight
   * onto the slate would barely register from overhead.
   */
  PIGEON_PALE: 0xc3cbd6,
  /** The two black wing bars, and the tail band. */
  PIGEON_BAR: 0x39404b,
  /**
   * The iridescent throat. A real pigeon's neck is green-to-violet oil-slick,
   * and it is the one thing that says "pigeon" rather than "grey bird" — worth
   * a colour of its own even though it is four voxels on a small animal.
   */
  PIGEON_SHEEN: 0x3f7a63,
  /** Beak, and the feet. Coral, and the only warm note on the bird. */
  PIGEON_CORAL: 0xd8836b,
} as const;

/**
 * Per-character darker tone for ears, paws, and tails. Kept as a lookup so the
 * critters get the same two-tone treatment as the dog without three more
 * near-identical palette entries.
 */
export const CHARACTER_DARK = {
  cat: 0xb36a30,
  fox: 0x9b3f2a,
  squirrel: 0x7d6144,
  pigeon: 0x5d6774,
} as const;

/**
 * Street trees and park planting.
 *
 * The leaf greens are deliberately deeper and bluer than `GROUND_COLORS.GRASS`.
 * Half the trees stand *on* the park grass, and at a matching green the canopy
 * vanishes into the lawn from this near-overhead camera.
 */
export const FOLIAGE = {
  BARK: 0x6b5140,
  /** Picked per tree from its position, so a row never repeats in lockstep. */
  LEAF: [0x4f8449, 0x5d9350, 0x6b8f42] as const,
  /** Terracotta window boxes under the first-floor windows. */
  PLANTER: 0xa9634a,
  /**
   * What's growing in them. These break rule 2 on purpose and get away with it
   * because each one is a few units across: a window box is the smallest thing
   * on a facade and the only place a hot colour can go without the building
   * starting to compete with the walker.
   */
  BLOOMS: [0xd9534f, 0xe0a03c] as const,
  BOX_LEAF: 0x4a7a43,
  /**
   * Hedge and bush greens for the block interiors. Duller and greyer than the
   * tree canopies on purpose: a garden hedge in the middle distance behind a
   * cottage must not compete with the trees lining the street, and this camera
   * shows both in the same frame constantly.
   */
  BUSH: [0x4a7346, 0x577f4b, 0x62753f] as const,
  /**
   * Weeds and long grass. The yellow-green of something gone to seed —
   * deliberately off the mown-park green so a patch of it reads as *neglect*
   * rather than as more lawn.
   */
  WEED: [0x7d8a45, 0x8a8f4e, 0x6d7c3e] as const,
} as const;

/** Objective props and the leash. */
export const PROPS = {
  // The lead is red at both ends of its range — it's the one object the player
  // watches constantly, so it gets a colour of its own rather than blending into
  // the pale street. Tension lerps from the deep tone to the bright one, which
  // still reads as "straining" without the lead changing identity mid-walk.
  //
  // The range is deliberately wide because the walker's tee is red too: the
  // slack end has to sit clearly *darker* than the shirt so the cord reads as a
  // separate object where it leaves his hand, and the taut end clearly hotter.
  LEASH_SLACK: 0x8f3630,
  LEASH_TAUT: 0xf04b32,
  COLLAR: 0x8f4a3c, // the dog's leather collar, where the lead clips on
  COLLAR_TAG: 0xd9b45a, // brass name tag
  BIN: 0x4a5750, // dark green municipal bin
  BIN_LID: 0x39443e,
  /**
   * The recess in the lid. Much darker than either — from overhead the lid is
   * most of what you see of a bin, and a mouth that reads as a real opening is
   * what identifies the object at a glance.
   */
  BIN_MOUTH: 0x212a26,
  BAG: 0xf7f2e8,
  HOME: 0x5ab473, // home-zone pad
  DOORMAT: 0x7c5a3c, // warm coir brown
  DOORMAT_BONE: 0xf5f2eb, // bone white
  PHONE_BOX: 0xc9362c, // classic British red phone kiosk
  PHONE_BOX_GLASS: 0xd9eaef, // light window glass
  POSTBOX: 0xba2d24, // red pillar box
  POSTBOX_GOLD: 0xd9b45a, // brass emblem / aperture detail
  /**
   * The posting slot. As dark as the bin's mouth and for the same reason: from
   * overhead a hole has to read as a hole, or the box is a red bollard.
   */
  POSTBOX_SLOT: 0x2a1c1a,
  FOUNTAIN_STONE: 0xb5b2ab, // weathered stone basin
  FOUNTAIN_WATER: 0x5b9ebd, // clear blue water
  /** The jet, broken up — near-white, so the fountain has something moving in it. */
  FOUNTAIN_FOAM: 0xeaf4f7,
  BENCH_WOOD: 0x6e4a2d, // dark wood bench
  BENCH_IRON: 0x3d4740, // cast iron frame
  /** Car body paints — same family as the door paints, so traffic fits in. */
  CAR_TINTS: [0xb8433c, 0x3a5570, 0xc9963f, 0x8d8d92, 0x4f8a63] as const,
  /**
   * Glass. Cool where the rest of the palette is warm, on purpose: it's standing
   * in for a windscreen full of reflected sky, and it's the one part of a car
   * that shouldn't share a family with the paint.
   *
   * It is *not* the whole cabin. The car used to be a body-coloured tub with a
   * single dark block on top, and from this camera that reads as a hole in the
   * roof rather than as windows — the roof stays painted and the glass is the
   * screens and the side band only.
   */
  CAR_GLASS: 0x2b3138,
  CAR_TYRE: 0x1c1a19,
  /** Bumpers, mirrors and the grille — one dark neutral for all the fittings. */
  CAR_TRIM: 0x3f4149,
  CAR_LAMP: 0xfff6c8,
  CAR_TAIL_LAMP: 0xd94a3a,
  CAR_BLINKER: 0xffa500,
  /** The sill rail under the doors, as a drop of the car's own paint. */
  CAR_SILL_DARKEN: -0.32,
  /**
   * The roof, as a lift of it — the panel the camera sees most of. Kept small:
   * at 0.1 a red car's roof goes visibly pink and reads as a different part
   * rather than as the same paint catching more sun.
   */
  CAR_ROOF_LIGHTEN: 0.06,
} as const;

/** Short-lived effect colours. Legibility first — these may break the palette. */
export const FX = {
  POOP: 0x7a5a34,
  SPARKLE: 0xffef9f,
  /** The dog's own progress ring while it goes — the poop's colour, lifted to read on grass.
   *  The one progress colour left: a green `PROGRESS` sat beside it for the
   *  cleanup hold's meter, and that hold is gone (see `types.ts` → `Objective`). */
  POOP_PROGRESS: 0xc79a5c,
  /** The dog losing its mind over a critter: the bark rings and the game-over burst. */
  FRENZY: 0xff8a8a,
  BIN_POP: 0xffe08a,
  CONFETTI: [0xff6b6b, 0xffd166, 0x6bd6a0, 0x6bb5ff, 0xd48bff] as const,
} as const;
